import React, { useState, useEffect } from "react";
import {
  getFirestore,
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import PortalSubjectNav from "../../../components/PortalSubjectNav";

const slugToTitle = (s = "") =>
  s.replace(/-/g, " ")
   .replace(/\b\w/g, (m) => m.toUpperCase());

// normalize "1st", "1st grade", "1st,science", etc. -> "1st"
const normalizeGrade = (raw = "") => {
  const s = String(raw).toLowerCase();
  const m = s.match(/\b(k|prek|pre\-k|[0-9]+(?:st|nd|rd|th))\b/);
  return m ? m[1].replace("pre-k", "prek") : "2nd";
};

// Pick the best matching subjectKey from the keys that actually exist in userData
const deriveSubjectKey = (progress = {}, student = {}, subject = "science") => {
  const keys = Object.keys(progress);
  const state = (student.state || "va").toLowerCase();
  const grade = normalizeGrade(student.grade || "2nd");
  const exact = `${state}_${grade}_${subject}`;
  if (progress[exact]) return exact;

  // fuzzy match: same grade & subject anywhere in key
  const fuzzyGrade = keys.find(k => k.endsWith(`_${subject}`) && k.includes(`_${grade}_`));
  if (fuzzyGrade) return fuzzyGrade;

  // fallback: any key for this subject
  const anySubject = keys.find(k => k.endsWith(`_${subject}`));
  if (anySubject) return anySubject;

  // last resort: return the exact we would expect (may not exist)
  return exact;
};

const Va2ndEducatorClassroom = () => {
  const navigate = useNavigate(); // Moved inside the component
  const [user, setUser] = useState(null);
  const [students, setStudents] = useState([]);
  const [selectedStudentUid, setSelectedStudentUid] = useState(null);
  const [selectedStudentProgress, setSelectedStudentProgress] = useState({});
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [breakdownTitle, setBreakdownTitle] = useState("");
  const [breakdownAnswers, setBreakdownAnswers] = useState([]);
  const db = getFirestore();
  const auth = getAuth();

  const brightStar = "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2Fbright_star.png?alt=media&token=b033c5b7-788e-401c-b650-98399c1e8551";
  const blankStar = "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2Fblank_star.png?alt=media&token=0dd0e339-3016-4f1c-8db2-eae91a16c21a";

  const scitools = [
    { title: "Motion", slug: "motion" },
    { title: "Adaptations", slug: "adaptations" },
    // Add the rest of your topics
  ];

  const handleHomeClick = () => {
    if (user) {
      const role = user.role.toLowerCase();
      if (role.includes("student")) {
        navigate("/dashboard");
      } else if (role.includes("educator")) {
        navigate("/educator/dashboard");
      } else if (role.includes("admin") || role.includes("administrator")) {
        navigate("/admin");
      } else {
        console.error("Unknown user role:", role);
      }
    } else {
      console.error("User is not defined.");
    }
  };
  
  const handleSubjectClick = (subject) => {
    const path =
      subject === "socialstudies"
        ? "/educator/va/va-2nd-educator-socialstudies"
        : `/educator/va/va-2nd-educator-${subject}`;
    navigate(path);
  };
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        const userRef = doc(db, "users", currentUser.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const userData = userSnap.data();
          setUser({ ...userData, uid: currentUser.uid });
          fetchStudents(userData.educatorCode);
        } else {
          console.error("User not found in Firestore.");
        }
      }
    });

    return () => unsubscribe();
  }, []);

  const fetchStudents = async (educatorCode) => {
    if (!educatorCode) return;
    try {
      const studentQuery = query(
        collection(db, "users"),
        where("role", "==", "va-2nd-student"),
        where("educatorCode", "==", educatorCode)
      );
      const snapshot = await getDocs(studentQuery);
      const studentsData = snapshot.docs.map((doc) => ({ uid: doc.id, ...doc.data() }));
      setStudents(studentsData);
    } catch (error) {
      console.error("Error fetching students:", error);
    }
  };

  const handleStudentClick = async (student) => {
    setSelectedStudentUid(student.uid);

    try {
      const educatorUid = auth.currentUser?.uid;
const educatorUserSnap = await getDoc(doc(db, "users", educatorUid));
const studentUserSnap  = await getDoc(doc(db, "users", student.uid));

console.log("== RULES DEBUG ==");
console.log("educator auth uid:", educatorUid);
console.log("educator exists:", educatorUserSnap.exists(), educatorUserSnap.data());
console.log("student  exists:", studentUserSnap.exists(), studentUserSnap.data());
console.log("educator role:", (educatorUserSnap.data()?.role || "(none)"));
console.log("educator code:", (educatorUserSnap.data()?.educatorCode || "(none)"));
console.log("student  code:", (studentUserSnap.data()?.educatorCode || "(none)"));

      const snap = await getDoc(doc(db, "userData", student.uid));
      console.log(
        "userData uid:",
        student.uid,
        "exists:",
        snap.exists(),
        "keys:",
        Object.keys(snap.data() || {})
      );
      if (snap.exists()) {
        setSelectedStudentProgress(snap.data());
      } else {
        setSelectedStudentProgress({});
      }
    } catch (error) {
      console.error("Error fetching student progress:", error);
      setSelectedStudentProgress({});
    }
  };

  const openBreakdown = (title, answers = []) => {
    setBreakdownTitle(title);
    setBreakdownAnswers(Array.isArray(answers) ? answers : []);
    setShowBreakdown(true);
  };
  const closeBreakdown = () => setShowBreakdown(false);

  return (
    <div style={styles.container}>
      {user && (
        <>
          {/* ✅ Logo */}
          <img
            src="https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2FSBLLogoBlackTM.png?alt=media&token=2f8fde38-b373-4367-9ade-a7b68bae3b6f"
            alt="Logo"
            style={styles.logo}
          />
  
          {/* ✅ Navigation Buttons */}
          {/* Was five hand-styled squares with Home first, routing to the
              static /educator/va/va-2nd-educator-* pages.
              Same tiles as the portal now, Home last, and every subject opens
              the dynamic portal — including Other. */}
          <PortalSubjectNav
            state="va"
            grade="2nd"
            view="educator"
            onHomeClick={handleHomeClick}
          />
  
          {/* ✅ Styled Educator Info */}
          <h1 style={styles.title}>
            <span style={{
              /* Was 22px UPPERCASE with 1px letter-spacing on a pale amber
                 chip — small, stretched, low contrast, and shouting. Wider
                 tracking makes short text harder to read, not easier, and
                 #b45309 on #fef3c7 is thin at that size on a projector.
                 Sentence case, more weight, more contrast, more padding. */
              fontSize: "clamp(15px, 2.2vw, 19px)",
              display: "inline-block",
              marginBottom: "12px",
              padding: "7px 18px",
              backgroundColor: "#fde68a",
              color: "#78350f",
              fontWeight: "700",
              borderRadius: "999px",
              letterSpacing: "0.01em",
              boxShadow: "0 2px 6px rgba(0,0,0,0.12)"
            }}>
              {user.firstName}'s Educator Portal
            </span>
            <br />
            <span style={{
              /* clamp() rather than a fixed 40px: this line is long
                 ("Virginia 3rd Grade Classroom") and at 40px it wrapped
                 mid-phrase on a phone. The text-shadow is lighter too — at
                 this size the old one blurred the letterforms rather than
                 lifting them. */
              fontSize: "clamp(24px, 5vw, 40px)",
              fontWeight: "800",
              color: "#0a3d62",
              textShadow: "0 1px 2px rgba(0,0,0,0.14)",
              display: "block",
              marginBottom: "14px",
              lineHeight: 1.2,
              maxWidth: "18ch",
              marginInline: "auto",
            }}>
              Virginia 2nd Grade Classroom
            </span>
          </h1>
  
          {/* ✅ Educator Contact Info */}
          <p>{user.sirName} {user.firstName} {user.lastName}</p>
          <p>{user.email}</p>
          {user.schoolName && <p>{user.schoolName}</p>}
        </>
      )}
  

      <div style={styles.studentContainer}>
        <h2>Our Future!</h2>
        {students.length === 0 ? (
          <p>No students found with your educator code.</p>
        ) : (
          <ul style={{ listStyleType: "none", padding: 0 }}>
            {students.map((student, index) => (
              <li key={index} style={styles.studentItem}>
                {student.avatarUrl && (
                  <img
                    src={student.avatarUrl}
                    alt={`${student.firstName}'s avatar`}
                    style={styles.avatar}
                  />
                )}
                {student.firstName} {student.lastName?.charAt(0)}.
              </li>
            ))}
          </ul>
        )}
      </div>

      <div style={styles.progressContainer}>
        <h2 style={{ color: "black" }}>Student Progress</h2>
        {user && (
          <>
            <h2 style={{ color: "black" }}>{user.sirName} {user.firstName} {user.lastName}'s Class</h2>
            <h3>Educator Code: {user.educatorCode}</h3>
          </>
        )}
        <h2>Student List</h2>
        <div style={styles.studentButtons}>
          {students.length === 0 ? (
            <p>No students found with the specified criteria.</p>
          ) : (
            students.map((student, index) => (
              <div key={index}>
                <button
                  onClick={() => handleStudentClick(student)}
                  style={{
                    ...styles.studentButton,
                    backgroundColor: `hsl(${(index * 40) % 360}, 70%, 80%)`,
                  }}
                >
                  {student.firstName}
                </button>

                {selectedStudentUid === student.uid && (
                  <div style={{ marginTop: "15px", textAlign: "left" }}>
                    <img src={student.avatarUrl} alt="avatar" style={{ width: 50, height: 50, borderRadius: "50%" }} />
                    <h4 style={{ margin: "8px 0" }}>
  {student.firstName} {student.lastName?.charAt(0)}.
  {student?.email ? ` (${student.email})` : ""}
</h4>
                    
                    <table style={{ width: "100%", marginTop: "10px", borderCollapse: "collapse" }}>
                      <thead>
                        <tr>
                          <th align="left">Title</th>
                          <th>Stars</th>
                          <th>Video Qty</th>
                          <th>Music Qty</th>
                          <th>Quiz Score</th>
                          <th>Total Score</th>
                        </tr>
                      </thead>
                      <tbody>
  {(() => {
    const docData = selectedStudentProgress || {};
    const topKeys = Object.keys(docData);

    // show what we actually got back (helps verify you’re on the right UID)
    // You can remove this after you see the expected key.
    const DebugKeysRow = () => (
      <tr>
        <td colSpan={6} style={{ padding: 8, fontSize: 12, color: "#555" }}>
          <div><strong>userData keys:</strong> {topKeys.length ? topKeys.map(k => `"${k}"`).join(", ") : "(none)"}</div>
        </td>
      </tr>
    );

    // pick a subject bucket by looking for `_science` anywhere in the key
    const normalize = (s) => String(s || "").toLowerCase().replace(/\s+/g, "");
    const subjectKey =
      topKeys.find(k => normalize(k).includes("_science")) ||
      ""; // nothing matched

    if (!subjectKey) {
      return (
        <>
          <DebugKeysRow />
          <tr>
            <td colSpan={6} style={{ padding: 8, color: "#666" }}>
              No science bucket found in this userData doc.
            </td>
          </tr>
        </>
      );
    }

    const subjectData = docData[subjectKey] || {};
    const slugs = Object.keys(subjectData)
      .filter((k) => subjectData[k] && typeof subjectData[k] === "object")
      .sort();

    if (slugs.length === 0) {
      return (
        <>
          <DebugKeysRow />
          <tr>
            <td colSpan={6} style={{ padding: 8, color: "#666" }}>
              Found bucket <code>{subjectKey}</code> but no topic entries.
            </td>
          </tr>
        </>
      );
    }

    const nice = (s) => (s || "").replace(/-/g, " ").replace(/\b\w/g, m => m.toUpperCase());

    return (
      <>
        <DebugKeysRow />
        {slugs.map((slug) => {
          const topicProgress = subjectData[slug] || {};
          const game = topicProgress.game || {};

          // what you asked to show
          const quizScore =
            typeof game.latestQuiz === "number" ? game.latestQuiz :
            typeof game.quizScore === "number" ? game.quizScore : 0;

          const totalScore =
            typeof game.latestTotal === "number" ? game.latestTotal :
            Array.isArray(game.score) && game.score.length ? game.score[game.score.length - 1] : 0;

          // stars/qty
          const videoQty = topicProgress.videoQty || 0;
          const musicQty = topicProgress.musicQty || 0;

          const videoStar = topicProgress.video ? brightStar : blankStar;
          const musicStar = topicProgress.music ? brightStar : blankStar;
          const gameStar  = topicProgress.game  ? brightStar : blankStar;

          const answers = game.latestDetail?.answers || [];

          return (
            <tr key={slug}>
              <td>
                <button
                  onClick={() => openBreakdown(nice(slug), answers)}
                  style={{ background: "none", border: "none", color: "#1d4ed8", cursor: "pointer", textDecoration: "underline", padding: 0 }}
                  title="View question-by-question breakdown"
                >
                  {nice(slug)}
                </button>
              </td>
              <td>
                <img src={videoStar} alt="Video Star" width={20} />
                <img src={musicStar} alt="Music Star" width={20} />
                <img src={gameStar}  alt="Game Star"  width={20} />
              </td>
              <td align="center">{videoQty}</td>
              <td align="center">{musicQty}</td>
              <td align="center">{quizScore}</td>
              <td align="center">{totalScore}</td>
            </tr>
          );
        })}
      </>
    );
  })()}
</tbody>


                    </table>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {showBreakdown && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000
        }}>
          <div style={{
            width: "min(900px, 92vw)", maxHeight: "80vh", overflow: "auto",
            background: "#fff", borderRadius: 10, padding: 20, boxShadow: "0 10px 30px rgba(0,0,0,.3)"
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <h3 style={{ margin: 0 }}>{breakdownTitle} — Quiz Breakdown</h3>
              <button onClick={closeBreakdown} style={{ border: "none", background: "#ef4444", color: "#fff", borderRadius: 6, padding: "6px 10px", cursor: "pointer" }}>Close</button>
            </div>
      
            {breakdownAnswers.length === 0 ? (
              <p style={{ margin: "12px 0" }}>No per-question detail stored for this attempt yet.</p>
            ) : (
              <ol style={{ paddingLeft: 18 }}>
                {breakdownAnswers.map((a, i) => (
                  <li key={i} style={{ marginBottom: 12, lineHeight: 1.4 }}>
                    <div style={{ fontWeight: 600 }}>{a.question || `Question ${a.qIndex + 1}`}</div>
                    <div>
                      Student:&nbsp;
                      <span style={{ fontWeight: 600, color: a.correct ? "#16a34a" : "#dc2626" }}>
                        {a.selectedText ?? "(no answer)"}
                      </span>
                    </div>
                    {!a.correct && (
                      <div>
                        Correct:&nbsp;<span style={{ fontWeight: 600 }}>{a.correctText ?? "(unknown)"}</span>
                      </div>
                    )}
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>
      )}

      <footer style={styles.footer}>
        <p style={{ margin: 0 }}>© Smart Board Learning™</p>
        <p style={{ margin: 0 }}>All rights reserved.</p>
      </footer>
    </div>
  );
};

const baseNavButton = {
  color: "#fff",
  padding: "clamp(8px, 2vw, 10px) clamp(10px, 3vw, 20px)",
  fontSize: "clamp(14px, 4vw, 25px)",    // ⬅️ scales down on phones
  border: "none",
  borderRadius: "5px",
  cursor: "pointer",
  width: "min(42vw, 200px)",             // ⬅️ responsive width
  height: "min(42vw, 200px)",            // ⬅️ responsive height
  aspectRatio: "1 / 1",                  // keep square if browser supports it
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  textAlign: "center",
  boxShadow: "0 6px 0 rgba(0, 0, 0, 0.4), 0 8px 10px rgba(0, 0, 0, 0.4)",
  border: "2px solid #222",
  transition: "transform 0.2s, box-shadow 0.2s",
  boxSizing: "border-box",
};

const styles = {
  container: { padding: "30px", textAlign: "center", fontFamily: "Arial" },
  studentContainer: { backgroundColor: "#e6f7ff", padding: "20px", borderRadius: "10px", marginBottom: "30px" },
  studentItem: { fontSize: "18px", margin: "5px 0", color: "#333" },
  studentButtons: { display: "flex", flexWrap: "wrap", gap: "10px", justifyContent: "center" },
  studentButton: { padding: "10px 20px", border: "none", borderRadius: "5px", cursor: "pointer" },
  progressContainer: { backgroundColor: "#fff", padding: "20px", borderRadius: "10px", boxShadow: "0 0 10px rgba(0,0,0,0.1)" },
  avatar: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    marginRight: "10px",
    verticalAlign: "middle",
    objectFit: "cover",
  },
  logo: {
    width: "150px",
    marginBottom: "15px"
  },
  navButtons: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexWrap: "wrap",          // ⬅️ allow wrapping
    gap: "12px",
    marginTop: "20px",
  },
  
  homeButton: {
    backgroundColor: "#9b59b6", // Purple
    color: "#fff",
    padding: "10px 15px",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer"
  },
  scienceButton: {
    backgroundColor: "#3498db", // Blue
    color: "#fff",
    padding: "10px 15px",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer"
  },
  socialStudiesButton: {
    backgroundColor: "#27ae60", // Green
    color: "#fff",
    padding: "10px 15px",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer"
  },
  mathButton: {
    backgroundColor: "#e74c3c", // Reddish
    color: "#fff",
    padding: "10px 15px",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer"
  },
  otherButton: {
    backgroundColor: "#f39c12", // Yellowish
    color: "#fff",
    padding: "10px 15px",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer"
  },
  title: {
    marginBottom: "20px"
  },
  
  footer: {
    marginTop: "40px",
    fontSize: "16px",
    color: "#555",
    textAlign: "center",
    padding: "10px",
    backgroundColor: "#f9f9f9",
    borderTop: "1px solid #ddd",
  },
};

export default Va2ndEducatorClassroom;
